import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useState} from 'react';

import {Button, Icon, view} from '@sail/ui';
import {cancelCircle} from '@sail/icons/react/Icon';

import {Conversation} from '@stripe-internal/conversations-client';
import {
  TopBar,
  useConversationUIFeatureFlags,
} from '@stripe-internal/conversations-ui';

import {ConversationScreenshareButton} from './ConversationScreenshareButton';
import {ConversationSpeakerButton} from './ConversationSpeakerButton';
import {useApp} from '../../../core/hooks/appHooks';

const messages = defineMessages({
  chattingWithName: {
    id: 'conversations_assistant.liveChatTopBar.chattingWithName',
    description:
      'Text indicating name of support agent the user is chatting with',
    defaultMessage: 'Chatting with {name}',
  },
  connectingWithAgent: {
    id: 'conversations_assistant.liveChatTopBar.chattingWithGeneric',
    description: 'Text indicating who the user is chatting stripe support',
    defaultMessage: 'Connecting with agent',
  },
  endChatButton: {
    id: 'conversations_assistant.liveChatTopBar.endChatButton',
    description: 'Button to end conversation with an agent',
    defaultMessage: 'End chat',
  },
});

function agentParticipantNames(conversation: Conversation) {
  const participants = Object.values(conversation.participants);

  const names = participants
    .filter((p) => p.role === 'agent')
    .map((p) => p.displayName)
    .filter((name) => !!name);

  return names;
}

type Props = {
  conversation: Conversation;
};

type TopBarProps = Props & {
  onClose: () => void;
  agentNames: string[];
};

function Title({agentNames}: {agentNames: string[]}) {
  return (
    <view.div css={{font: 'label.small.emphasized'}}>
      {agentNames.length > 0 ? (
        <FormattedMessage
          {...messages.chattingWithName}
          values={{name: agentNames.join(', ')}}
        />
      ) : (
        <FormattedMessage {...messages.connectingWithAgent} />
      )}
    </view.div>
  );
}

function Controls({
  conversation,
  onClose,
}: Props & {
  onClose: () => void;
}) {
  return (
    <view.div css={{stack: 'x', alignX: 'end'}}>
      <ConversationSpeakerButton conversation={conversation} />
      <Button
        size="small"
        onPress={onClose}
        data-testid="conversations-widget-end-chat-button"
      >
        <FormattedMessage {...messages.endChatButton} />
      </Button>
    </view.div>
  );
}

function ControlsScreenshare({conversation, onClose, agentNames}: TopBarProps) {
  return (
    <view.div
      css={{
        stack: 'x',
        gap: 'small',
        width: 'fill',
      }}
    >
      <ConversationScreenshareButton agentNames={agentNames} channel="chat" />
      <Button
        size="small"
        onPress={onClose}
        data-testid="conversations-widget-end-chat-button"
        css={{flex: 1, gap: 'small', height: '28px'}}
      >
        <Icon icon={cancelCircle} />
        <FormattedMessage {...messages.endChatButton} />
      </Button>
      <ConversationSpeakerButton
        conversation={conversation}
        screenshareRender
      />
    </view.div>
  );
}

function TopBarWithoutScreenshare({
  conversation,
  agentNames,
  onClose,
}: TopBarProps) {
  return (
    <TopBar data-testId="widget-topbar-live-chat">
      <view.div css={{stack: 'x'}}>
        <Title agentNames={agentNames} />
        <Controls conversation={conversation} onClose={onClose} />
      </view.div>
    </TopBar>
  );
}

function TopBarWithScreenshare({
  conversation,
  onClose,
  agentNames,
}: Props & {
  onClose: () => void;
  agentNames: string[];
}) {
  return (
    <TopBar data-testId="widget-topbar-live-chat">
      <view.div css={{stack: 'y', gap: 'small'}}>
        <Title agentNames={agentNames} />
        <view.div css={{stack: 'x'}}>
          <ControlsScreenshare
            conversation={conversation}
            onClose={onClose}
            agentNames={agentNames}
          />
        </view.div>
      </view.div>
    </TopBar>
  );
}

export function LiveChatTopBar({conversation}: Props) {
  const app = useApp();
  const {enableScreenshareUi} = useConversationUIFeatureFlags();

  const [agentNames, setAgentNames] = useState<string[]>(
    agentParticipantNames(conversation),
  );
  const onClose = useCallback(() => {
    app.close();
  }, [app]);

  useEffect(() => {
    const sub = conversation
      .participantsObservable()
      .subscribe(() => setAgentNames(agentParticipantNames(conversation)));

    return () => sub.unsubscribe();
  }, [conversation]);

  if (enableScreenshareUi) {
    return (
      <TopBarWithScreenshare
        conversation={conversation}
        onClose={onClose}
        agentNames={agentNames}
      />
    );
  }

  return (
    <TopBarWithoutScreenshare
      conversation={conversation}
      agentNames={agentNames}
      onClose={onClose}
    />
  );
}
